import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Clock, MapPin, DollarSign, Pencil, X, ToggleRight, ToggleLeft, CalendarOff } from "lucide-react";

const WEEKDAYS = [
  { v: 0, label: "Mon", long: "Monday" },
  { v: 1, label: "Tue", long: "Tuesday" },
  { v: 2, label: "Wed", long: "Wednesday" },
  { v: 3, label: "Thu", long: "Thursday" },
  { v: 4, label: "Fri", long: "Friday" },
  { v: 5, label: "Sat", long: "Saturday" },
  { v: 6, label: "Sun", long: "Sunday" },
];

const DUR_OPTIONS = [30, 45, 60, 75, 90, 120];

export default function CoachAvailability() {
  const [rules, setRules] = useState([]);
  const [openFor, setOpenFor] = useState(null); // weekday number to add window for
  const [editRule, setEditRule] = useState(null);
  const [form, setForm] = useState({
    weekday: 0, start_time: "09:00", end_time: "12:00",
    slot_duration_mins: 60, location: "", price: 0, active: true,
  });
  const [slotPreview, setSlotPreview] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [newException, setNewException] = useState({ date: "", reason: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/availability");
      setRules(data || []);
    } catch (_) {}
  };
  const loadPreview = async () => {
    try {
      const { data } = await api.get("/availability/slots", { params: { days: 28 } });
      setSlotPreview(data || []);
    } catch (_) {}
  };
  const loadExceptions = async () => {
    try {
      const { data } = await api.get("/availability/exceptions");
      setExceptions(data || []);
    } catch (_) {}
  };
  useEffect(() => { load(); loadPreview(); loadExceptions(); }, []);

  const byDay = WEEKDAYS.map((w) => ({ ...w, rules: rules.filter((r) => r.weekday === w.v) }));

  const startAdd = (weekday) => {
    setEditRule(null);
    setForm({ weekday, start_time: "09:00", end_time: "12:00", slot_duration_mins: 60, location: "", price: 0, active: true });
    setOpenFor(weekday);
  };

  const startEdit = (r) => {
    setEditRule(r);
    setForm({
      weekday: r.weekday, start_time: r.start_time, end_time: r.end_time,
      slot_duration_mins: r.slot_duration_mins, location: r.location || "",
      price: r.price || 0, active: r.active,
    });
    setOpenFor(r.weekday);
  };

  const closeForm = () => { setOpenFor(null); setEditRule(null); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, slot_duration_mins: Number(form.slot_duration_mins) || 60, price: Number(form.price) || 0 };
      if (editRule) {
        await api.patch(`/availability/${editRule.id}`, body);
        toast.success("Window updated");
      } else {
        await api.post("/availability", body);
        toast.success("Window added");
      }
      closeForm();
      load(); loadPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/availability/${r.id}`, { active: !r.active });
      load(); loadPreview();
    } catch (_) { toast.error("Could not toggle"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${WEEKDAYS[r.weekday].long} ${r.start_time}–${r.end_time}?`)) return;
    try {
      await api.delete(`/availability/${r.id}`);
      toast.success("Removed");
      load(); loadPreview();
    } catch (_) { toast.error("Could not remove"); }
  };

  const addException = async (e) => {
    e.preventDefault();
    if (!newException.date) { toast.error("Pick a date"); return; }
    try {
      await api.post("/availability/exceptions", { date: newException.date, reason: newException.reason });
      toast.success("Day off added");
      setNewException({ date: "", reason: "" });
      loadExceptions(); loadPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  };

  const deleteException = async (ex) => {
    if (!window.confirm(`Restore availability on ${new Date(ex.date + 'T00:00').toLocaleDateString(undefined, { weekday:'short', month:'short', day:'numeric', year:'numeric' })}?`)) return;
    try {
      await api.delete(`/availability/exceptions/${ex.id}`);
      toast.success("Day restored");
      loadExceptions(); loadPreview();
    } catch (_) { toast.error("Could not remove"); }
  };

  return (
    <div className="space-y-8" data-testid="coach-availability-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Weekly windows</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">Availability</h1>
          <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
            Define when you're open for private lessons. Students self-book {DUR_OPTIONS[2]}-minute blocks (or whatever duration you choose)
            from these windows. Already-booked times are skipped automatically.
          </p>
        </div>
      </div>

      {/* Weekday grid */}
      <div className="grid gap-3">
        {byDay.map((day) => (
          <div key={day.v} className="card-surface p-5" data-testid={`weekday-${day.v}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-3">
                <div className="font-display text-2xl font-black uppercase tracking-tight w-32">{day.long}</div>
                {day.rules.length === 0 && <span className="text-xs text-[#A3A3A3] italic">Unavailable</span>}
              </div>
              <button onClick={() => startAdd(day.v)} className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2" data-testid={`add-window-${day.v}`}>
                <Plus size={12} /> Add window
              </button>
            </div>

            {day.rules.length > 0 && (
              <div className="mt-4 space-y-2">
                {day.rules.map((r) => (
                  <div key={r.id} className={`flex items-center gap-4 border border-white/10 p-3 ${!r.active ? "opacity-50" : ""}`} data-testid={`rule-${r.id}`}>
                    <button onClick={() => toggle(r)} className="p-1" aria-label={r.active ? "Disable" : "Enable"} data-testid={`toggle-${r.id}`}>
                      {r.active ? <ToggleRight size={22} className="text-[#38BDF8]" /> : <ToggleLeft size={22} className="text-[#A3A3A3]" />}
                    </button>
                    <div className="flex items-center gap-2 text-sm flex-1">
                      <Clock size={14} className="text-[#38BDF8]" />
                      <span className="font-mono">{r.start_time} – {r.end_time}</span>
                      <span className="text-[#A3A3A3]">·</span>
                      <span>{r.slot_duration_mins}-min slots</span>
                      {r.location && <><span className="text-[#A3A3A3]">·</span><MapPin size={12} className="text-[#A3A3A3]" /><span className="text-[#A3A3A3]">{r.location}</span></>}
                      {r.price > 0 && <><span className="text-[#A3A3A3]">·</span><DollarSign size={12} className="text-[#A3A3A3]" /><span>{r.price}</span></>}
                    </div>
                    <button onClick={() => startEdit(r)} className="p-2 hover:bg-white/5" aria-label="Edit" data-testid={`edit-rule-${r.id}`}><Pencil size={13} /></button>
                    <button onClick={() => remove(r)} className="p-2 hover:bg-red-500/10 text-red-400" aria-label="Delete" data-testid={`delete-rule-${r.id}`}><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Inline form */}
            {openFor === day.v && (
              <form onSubmit={save} className="mt-4 p-4 border border-[#38BDF8]/50 bg-[#38BDF8]/5 space-y-3" data-testid={`form-${day.v}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-[#38BDF8] font-bold">
                    {editRule ? "Edit window" : `Add window · ${day.long}`}
                  </div>
                  <button type="button" onClick={closeForm} className="text-[#A3A3A3] hover:text-white" aria-label="Close" data-testid={`close-form-${day.v}`}><X size={16} /></button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="Start" type="time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testId={`form-start-${day.v}`} />
                  <Field label="End" type="time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testId={`form-end-${day.v}`} />
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Slot length</label>
                    <select
                      value={form.slot_duration_mins}
                      onChange={(e) => setForm({ ...form, slot_duration_mins: Number(e.target.value) })}
                      className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
                      data-testid={`form-duration-${day.v}`}
                    >
                      {DUR_OPTIONS.map((d) => <option key={d} className="bg-[#141414]" value={d}>{d} min</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Location (optional)" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId={`form-location-${day.v}`} />
                  <Field label="Price per slot ($)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testId={`form-price-${day.v}`} />
                </div>
                <button className="btn-primary py-2.5 px-5 text-xs" data-testid={`form-submit-${day.v}`}>{editRule ? "Save" : "Add window"}</button>
              </form>
            )}
          </div>
        ))}
      </div>

      {/* Days off — exception dates */}
      <div className="card-surface p-6" data-testid="days-off-card">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold flex items-center gap-2"><CalendarOff size={12} /> Days off</div>
            <h3 className="font-display text-2xl font-black uppercase tracking-tighter mt-1">Block specific dates</h3>
            <p className="text-xs text-[#A3A3A3] mt-2 max-w-xl">
              Weekly rules got you covered, but some Sundays you're at a tournament. Add those dates here —
              students won't see any slots on them.
            </p>
          </div>
        </div>

        <form onSubmit={addException} className="mt-4 flex flex-wrap items-end gap-3" data-testid="add-exception-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Date</label>
            <input
              type="date" value={newException.date}
              onChange={(e) => setNewException({ ...newException, date: e.target.value })}
              className="bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 px-1 text-sm"
              data-testid="exception-date-input"
              min={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Reason (optional)</label>
            <input
              type="text" value={newException.reason} placeholder="Tournament, travel, family…"
              onChange={(e) => setNewException({ ...newException, reason: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
              data-testid="exception-reason-input"
            />
          </div>
          <button className="btn-primary px-5 py-2.5 text-xs inline-flex items-center gap-2" data-testid="exception-submit">
            <Plus size={12} /> Mark day off
          </button>
        </form>

        {exceptions.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="exceptions-list">
            {exceptions.map((ex) => (
              <div key={ex.id} className="inline-flex items-center gap-2 border border-white/10 bg-white/5 px-3 py-1.5 text-xs" data-testid={`exception-${ex.id}`}>
                <CalendarOff size={12} className="text-[#38BDF8]" />
                <span className="font-mono">{new Date(ex.date + 'T00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                {ex.reason && <span className="text-[#A3A3A3]">· {ex.reason}</span>}
                <button onClick={() => deleteException(ex)} className="ml-2 text-[#A3A3A3] hover:text-red-400" aria-label="Restore" data-testid={`exception-delete-${ex.id}`}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming preview */}
      <div className="card-surface p-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-3">Next 14 days · open slots preview</div>
        {slotPreview.length === 0 ? (
          <div className="text-sm text-[#A3A3A3]">No open slots yet. Add a weekly window above.</div>
        ) : (
          <div className="flex flex-wrap gap-2 max-h-72 overflow-auto">
            {slotPreview.slice(0, 100).map((s, i) => (
              <div key={i} className="text-[11px] font-mono border border-white/10 px-2 py-1 text-[#A3A3A3]" data-testid={`preview-slot-${i}`}>
                {new Date(s.start).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            ))}
            {slotPreview.length > 100 && (
              <div className="text-[11px] text-[#A3A3A3] self-center">+ {slotPreview.length - 100} more…</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
        data-testid={testId}
      />
    </div>
  );
}
